import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BodyClassName from 'react-body-classname';
import { useSelector } from 'react-redux';

import { FeatureFlag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { ThemeType } from '@swf/themes/index';
import { getTheme } from '@swf/themes/index';

export const ThemeContext = React.createContext<{ darkClassName?: string }>({});

interface Props {
  theme?: ThemeType | null;
}

const ThemeProvider: React.FC<Props> = ({ children, theme }) => {
  const themeClass = useRef<string>();
  const [oldClass, setOldClass] = useState<string>();
  const [themeClassName, setThemeClassName] = useState<string>();
  const [darkClassName, setDarkClassName] = useState<string>();

  const isSiteUS = useSelector(select.cms.isSiteUS);
  const brand = useSelector(select.cms.getBrandId);
  const site = useSelector(select.cms.getSiteId);
  const region = useSelector(select.cms.getRegion);
  const skyBetSports = useFeature(FeatureFlag.SkyBetSports);

  useEffect(() => {
    if (oldClass) {
      document.body.className = document.body.className.replace(oldClass, '');
    }
  }, [oldClass]);

  const Theme = useMemo(
    () => theme || getTheme({ isSiteUS, brand, site, region, skyBetSports }),
    [theme, isSiteUS, brand, site, region, skyBetSports]
  );

  const onThemeChange = useCallback((themeClassName: string, darkClassName?: string) => {
    if (themeClass.current !== themeClassName) {
      setOldClass(themeClass.current);
      setThemeClassName(themeClassName);
      setDarkClassName(darkClassName);
      themeClass.current = themeClassName;
    }
  }, []);

  return (
    <>
      <Theme onThemeChange={onThemeChange} />

      <ThemeContext.Provider value={{ darkClassName }}>
        {themeClassName && <BodyClassName className={themeClassName} />}
        {children}
      </ThemeContext.Provider>
    </>
  );
};

export default ThemeProvider;
